import { compileScss } from "./sass";
import { preprocessDeck } from "./preprocess";
import { extractDeclarations } from "./frontmatter";
import { injectRevealConfigOverride, injectSandboxCompat, inlineFontUrls, inlineKatexAssets, inlineRevealAssets, inlineThemeCss } from "./inline-assets";
import type { PandocInstance, RenderInputs, RenderResult } from "./types";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg"]);
const FONT_EXTENSIONS = new Set(["woff2", "woff", "ttf", "otf"]);

function mimeFor(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return (
    {
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      woff2: "font/woff2",
      woff: "font/woff",
      ttf: "font/ttf",
      otf: "font/otf",
    }[ext] ?? "application/octet-stream"
  );
}

function bytesToDataUri(bytes: Uint8Array, mime: string): string {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return `data:${mime};base64,${btoa(bin)}`;
}

export async function renderDeck(
  pandoc: PandocInstance,
  inputs: RenderInputs,
): Promise<RenderResult> {
  const t0 = performance.now();

  // The stylesheet may be either Sass source (compile via Dart Sass) or
  // already-compiled CSS (use as-is). Empty string means no theme in the
  // project; skip the entire CSS plumbing in that case.
  let css = inputs.stylesheet
    ? inputs.stylesheetIsPrecompiled
      ? inputs.stylesheet
      : compileScss(inputs.stylesheet)
    : "";

  // Build the asset-URI maps: images get inlined into the markdown, fonts
  // get inlined into the compiled theme CSS. Both must become data URIs
  // because the deck renders in a null-origin sandboxed iframe where
  // relative URLs (and external CDN fetches) don't resolve.
  const assetUris = new Map<string, string>();
  const fontUris = new Map<string, string>();
  for (const [name, bytes] of inputs.assets) {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    if (IMAGE_EXTENSIONS.has(ext)) {
      assetUris.set(name, bytesToDataUri(bytes, mimeFor(name)));
    } else if (FONT_EXTENSIONS.has(ext)) {
      fontUris.set(name, bytesToDataUri(bytes, mimeFor(name)));
    }
  }

  // Embed self-hosted theme fonts as data URIs before the CSS is inlined.
  if (css) css = inlineFontUrls(css, fontUris);

  const { qmd } = preprocessDeck(inputs.qmd, { assetUris, includes: inputs.includes });

  const pandocFiles: Record<string, string | Blob> = {};
  if (css) pandocFiles["theme.css"] = css;
  if (inputs.bib) pandocFiles["references.bib"] = inputs.bib;

  const options: Record<string, unknown> = {
    from: "markdown",
    to: "revealjs",
    standalone: true,
    "slide-level": 2,
    "html-math-method": "katex",
    // theme=white loads reveal.js's white.css as the base, so an imago-style
    // SCSS file (which assumes a light base and overrides .dark/.light slide
    // variants) is no longer fighting the default black.css cascade.
    variables: { theme: "white" },
    ...(css ? { css: ["theme.css"] } : {}),
    ...(inputs.bib ? { citeproc: true, bibliography: "references.bib" } : {}),
  };

  const result = await pandoc.convert(options, qmd, pandocFiles);

  const warnings: string[] = [];
  for (const w of result.warnings) {
    if (typeof w === "string") warnings.push(w);
    else if (w && typeof w === "object" && "pretty" in w) warnings.push(String((w as { pretty: unknown }).pretty));
    else warnings.push(JSON.stringify(w));
  }

  // Five post-pandoc passes:
  //   1. Sandbox-compat polyfill (localStorage/sessionStorage/history shims so
  //      reveal.js plugins don't crash inside the sandboxed iframe).
  //   2. Reveal.js core + plugins (eliminates external CDN deps).
  //   3. KaTeX JS + CSS with fonts inlined as data URIs (eliminates the
  //      jsdelivr CDN dep; fonts work in the null-origin sandboxed iframe).
  //   4. Our compiled theme.css (pandoc only emits a <link> pointing at the
  //      VFS path, which the iframe can't resolve — see inlineThemeCss).
  //   5. The user's `format.revealjs.*` options applied via Reveal.configure()
  //      after init, so boolean-false options like `controls: false` actually
  //      take effect (pandoc's template skips them).
  const declared = extractDeclarations(inputs.qmd);
  let html = injectSandboxCompat(result.stdout);
  html = inlineRevealAssets(html);
  html = inlineKatexAssets(html);
  html = inlineThemeCss(html, css);
  html = injectRevealConfigOverride(html, declared.revealjsOptions);

  return {
    html,
    warnings,
    stderr: result.stderr,
    durationMs: performance.now() - t0,
  };
}
